import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Tag,
    Typography,
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckOutlined,
    DeleteOutlined,
    GiftOutlined,
    InboxOutlined,
    PictureOutlined,
    ShoppingOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import Dragger from 'antd/es/upload/Dragger';

import { IReward } from '../../../../interfaces';
import useRewardForm from './useRewardForm';
import RadioIsInactive from '../RadioIsInactive';
import PageHeader from '../../../../components/PageHeader';
import { ThemeContext } from '../../../../contexts/ThemeContext';

const { Text } = Typography;

interface IProps {
    action: 'Adicionar' | 'Editar';
    reward?: IReward;
    onSubmit: (city: FormData) => Promise<void>;
}

export default function RewardForm({ action, reward, onSubmit }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        handleSubmit,
        handleSubmitForm,
        control,
        isSubmiting,
        errors,
        handleChangeImage,
        handleRemoveImage,
        imageUrl,
        dummyRequest,
        categories,
        isLoadingCategories,
        ACCEPTED_IMAGE_TYPES,
    } = useRewardForm(onSubmit, reward);

    const isEdit = action === 'Editar';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Breadcrumb Bar */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    border: `1px solid ${isDark ? '#262626' : '#f0f0f0'}`,
                    padding: '10px 16px',
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/recompensas">Recompensas</Link>,
                        },
                        {
                            title: isEdit ? `Editar Recompensa #${reward?.id ?? ''}` : 'Nova Recompensa',
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<GiftOutlined />}
                iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                title={isEdit ? `Editar Recompensa: ${reward?.name || ''}` : 'Nova Recompensa'}
                tag={isEdit && reward?.id ? <Tag color="blue">ID #{reward.id}</Tag> : undefined}
                subtitle={
                    isEdit
                        ? 'Atualize as informações, pontuação necessária, estoque e status da recompensa'
                        : 'Cadastre uma nova recompensa no catálogo para resgate de pontos pelos cidadãos'
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/recompensas')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Recompensas
                    </Button>
                }
            />

            {/* Inactive Alert Banner */}
            {reward?.is_inactive && (
                <Alert
                    type="error"
                    showIcon
                    message="Essa recompensa está inativa!"
                    description="Recompensas inativas não aparecem para os cidadãos no catálogo de resgates do aplicativo."
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* Issued Vouchers Warning Banner */}
            {Boolean(reward?.vouchers_issued && reward.vouchers_issued > 0) && (
                <Alert
                    type="warning"
                    showIcon
                    message={
                        reward!.vouchers_issued === 1
                            ? `Existe 1 voucher emitido e pendente de retirada para esta recompensa!`
                            : `Existem ${reward!.vouchers_issued} vouchers emitidos e pendentes de retirada para esta recompensa!`
                    }
                    description="Certifique-se de que há estoque físico disponível no ponto de entrega para atender aos resgates já realizados."
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* Main Form Card */}
            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                    background: isDark ? '#141414' : '#ffffff',
                    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                }}
                bodyStyle={{ padding: '24px' }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    {/* Section 1: Dados Principais */}
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <GiftOutlined style={{ color: '#10b981', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Informações Básicas
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Identificação, categoria e descrição do item para os cidadãos
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={14}>
                                <Form.Item
                                    label={<strong>Nome da Recompensa</strong>}
                                    required
                                    validateStatus={errors.name ? 'error' : undefined}
                                    help={errors.name?.message}
                                >
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, digite o nome da recompensa!' },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: Garrafa Térmica 500ml, Squeeze, Ecobag Personalizada"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<GiftOutlined style={{ color: '#10b981' }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={10}>
                                <Form.Item
                                    label={<strong>Categoria</strong>}
                                    required
                                    validateStatus={errors.category_id ? 'error' : undefined}
                                    help={errors.category_id?.message}
                                >
                                    <Controller
                                        name="category_id"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, selecione a categoria da recompensa!' },
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={categories.map((category) => ({
                                                    value: category.id,
                                                    label: category.name,
                                                }))}
                                                placeholder="Selecione a categoria"
                                                size="large"
                                                style={{ width: '100%', borderRadius: 8 }}
                                                disabled={isLoadingCategories}
                                                loading={isLoadingCategories}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item
                                    label={<strong>Descrição Detalhada</strong>}
                                    required
                                    validateStatus={errors.description ? 'error' : undefined}
                                    help={errors.description?.message}
                                >
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, digite a descrição!' },
                                        }}
                                        render={({ field }) => (
                                            <Input.TextArea
                                                {...field}
                                                placeholder="Descreva os detalhes da recompensa, características do produto e orientações para o resgate..."
                                                rows={3}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 2: Pontuação & Estoque */}
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <ShoppingOutlined style={{ color: '#1677ff', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Pontuação, Estoque & Disponibilidade
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Defina o custo em pontos para resgate, saldo disponível e status de ativação
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={<strong>Pontos Necessários</strong>}
                                    required
                                    validateStatus={errors.points ? 'error' : undefined}
                                    help={errors.points?.message}
                                >
                                    <Controller
                                        name="points"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, informe a pontuação!' },
                                            min: { value: 0, message: 'A pontuação não pode ser negativa.' },
                                        }}
                                        render={({ field }) => (
                                            <InputNumber
                                                {...field}
                                                type="number"
                                                min={0}
                                                style={{ width: '100%', borderRadius: 8 }}
                                                size="large"
                                                placeholder="Ex: 50"
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={<strong>Quantidade em Estoque</strong>}
                                    required
                                    validateStatus={errors.stock ? 'error' : undefined}
                                    help={errors.stock?.message}
                                >
                                    <Controller
                                        name="stock"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, informe o estoque disponível!' },
                                            min: { value: 0, message: 'O estoque não pode ser negativo.' },
                                        }}
                                        render={({ field }) => (
                                            <InputNumber
                                                {...field}
                                                type="number"
                                                min={0}
                                                style={{ width: '100%', borderRadius: 8 }}
                                                size="large"
                                                placeholder="Ex: 100"
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={<strong>Status da Recompensa</strong>}
                                    required
                                    validateStatus={errors.is_inactive ? 'error' : undefined}
                                    help={errors.is_inactive?.message}
                                >
                                    <Controller
                                        name="is_inactive"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <RadioIsInactive {...field} />
                                            </div>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 3: Foto da Recompensa */}
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <PictureOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Foto da Recompensa
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Imagem exibida no aplicativo aos munícipes (formatos: PNG, JPG, JPEG, WEBP)
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={16}>
                                <Form.Item
                                    label={<strong>Arquivo de Imagem</strong>}
                                    validateStatus={errors.image ? 'error' : undefined}
                                    help={errors.image?.message}
                                >
                                    <Controller
                                        name="image"
                                        control={control}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <div>
                                                {imageUrl && typeof imageUrl === 'string' ? (
                                                    <div
                                                        style={{
                                                            border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                                            borderRadius: 10,
                                                            padding: 16,
                                                            background: isDark ? '#1f1f1f' : '#f8fafc',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            flexWrap: 'wrap',
                                                            gap: 16,
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                            <img
                                                                src={imageUrl}
                                                                alt="Imagem da recompensa"
                                                                style={{
                                                                    width: 80,
                                                                    height: 80,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 8,
                                                                    background: isDark ? '#141414' : '#ffffff',
                                                                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                                                    padding: 4,
                                                                }}
                                                            />
                                                            <div>
                                                                <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#f0f0f0' : '#1e293b' }}>
                                                                    Foto Carregada
                                                                </div>
                                                                <div style={{ fontSize: 11.5, color: isDark ? '#8c8c8c' : '#64748b', marginTop: 2 }}>
                                                                    Formatos aceitos: PNG, JPG, JPEG, WEBP
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <Dragger
                                                                {...field}
                                                                showUploadList={false}
                                                                customRequest={dummyRequest}
                                                                onChange={(e: UploadChangeParam<UploadFile>) =>
                                                                    handleChangeImage({ onChangeHookForm: onChange, file: e.file })
                                                                }
                                                                accept={ACCEPTED_IMAGE_TYPES.toString()}
                                                                style={{ padding: 0, border: 'none', background: 'transparent' }}
                                                            >
                                                                <Button icon={<UploadOutlined />} style={{ borderRadius: 6, fontWeight: 500 }}>
                                                                    Trocar Imagem
                                                                </Button>
                                                            </Dragger>
                                                            <Button
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handleRemoveImage(onChange)}
                                                                style={{ borderRadius: 6 }}
                                                            >
                                                                Remover
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Dragger
                                                        {...field}
                                                        showUploadList={false}
                                                        customRequest={dummyRequest}
                                                        onChange={(e: UploadChangeParam<UploadFile>) =>
                                                            handleChangeImage({ onChangeHookForm: onChange, file: e.file })
                                                        }
                                                        accept={ACCEPTED_IMAGE_TYPES.toString()}
                                                        style={{
                                                            borderRadius: 10,
                                                            border: `2px dashed ${
                                                                errors.image
                                                                    ? '#ff4d4f'
                                                                    : isDark
                                                                        ? '#303030'
                                                                        : '#d9d9d9'
                                                            }`,
                                                            background: isDark ? '#1f1f1f' : '#fafafa',
                                                            padding: '24px 16px',
                                                        }}
                                                    >
                                                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                            <InboxOutlined style={{ color: '#10b981', fontSize: 36 }} />
                                                        </p>
                                                        <p
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize: 13.5,
                                                                color: isDark ? '#f0f0f0' : '#1e293b',
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            Clique ou arraste a imagem da recompensa aqui
                                                        </p>
                                                        <p style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b', margin: 0 }}>
                                                            Formatos aceitos: PNG, JPG, JPEG e WEBP (máx. 5MB)
                                                        </p>
                                                    </Dragger>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider style={{ margin: '20px 0 16px 0' }} />

                    {/* Bottom Action Buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                        <Button
                            onClick={() => navigate('/recompensas')}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            {isEdit ? 'Salvar Alterações' : 'Cadastrar Recompensa'}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
